import { useEffect } from "react";

// Deterrent-level protection to discourage print / screenshot / image saving
// on public-facing configurator, funnel and client pages.
// NOTE: OS-level screenshots (PrintScreen, Cmd+Shift+4, phone photo) cannot be
// blocked 100% from the browser. This applies the strongest deterrents possible.
export default function useContentProtection(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const root = document.documentElement;
    root.classList.add("content-protected");

    // Black overlay used as screenshot/print deterrent
    const overlay = document.createElement("div");
    overlay.className = "content-protection-overlay";
    document.body.appendChild(overlay);

    let flashTimer = null;
    let safetyTimer = null;
    const hideOverlay = () => { overlay.style.display = "none"; if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; } };
    // Mostra l'overlay ma con un timer di sicurezza: non deve MAI restare nero in modo
    // permanente (es. su mobile aprendo fotocamera/file per la bolletta l'evento focus a
    // volte non torna). Dopo 1,5s l'overlay si nasconde comunque da solo.
    const showOverlay = () => {
      overlay.style.display = "block";
      if (safetyTimer) clearTimeout(safetyTimer);
      safetyTimer = setTimeout(hideOverlay, 1500);
    };
    const flashOverlay = () => {
      overlay.style.display = "block";
      if (flashTimer) clearTimeout(flashTimer);
      flashTimer = setTimeout(hideOverlay, 1000);
    };

    const blockContext = (e) => { e.preventDefault(); return false; };
    const blockDrag = (e) => { e.preventDefault(); return false; };

    const blockKeys = (e) => {
      const k = (e.key || "").toLowerCase();
      // Print / Save / Copy / Cut / View-source
      if ((e.ctrlKey || e.metaKey) && ["p", "s", "c", "x", "u"].includes(k)) {
        e.preventDefault();
        e.stopPropagation();
        if (k === "p") flashOverlay();
        return false;
      }
      // Windows PrintScreen
      if (k === "printscreen" || e.key === "PrintScreen") {
        try { navigator.clipboard && navigator.clipboard.writeText(""); } catch (_) {}
        flashOverlay();
        e.preventDefault();
        return false;
      }
      // macOS screenshot shortcuts Cmd+Shift+3/4/5
      if (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(k)) {
        flashOverlay();
        e.preventDefault();
        return false;
      }
    };

    const onBlur = () => { showOverlay(); };
    const onFocus = () => { hideOverlay(); };
    const onVisibility = () => { document.hidden ? showOverlay() : hideOverlay(); };
    const onBeforePrint = () => { showOverlay(); };
    const onAfterPrint = () => { hideOverlay(); };
    const onPageShow = () => { hideOverlay(); };
    const onInteract = () => { if (!document.hidden) hideOverlay(); };

    document.addEventListener("contextmenu", blockContext);
    document.addEventListener("dragstart", blockDrag);
    document.addEventListener("keydown", blockKeys, true);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("pointerdown", onInteract, true);
    document.addEventListener("touchstart", onInteract, true);
    document.addEventListener("click", onInteract, true);

    return () => {
      root.classList.remove("content-protected");
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("dragstart", blockDrag);
      document.removeEventListener("keydown", blockKeys, true);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("pointerdown", onInteract, true);
      document.removeEventListener("touchstart", onInteract, true);
      document.removeEventListener("click", onInteract, true);
      if (flashTimer) clearTimeout(flashTimer);
      if (safetyTimer) clearTimeout(safetyTimer);
      overlay.remove();
    };
  }, [enabled]);
}
